/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { gql, useQuery } from '@apollo/client';
import { Link } from '@apollo/orbit';
import { jsx } from '@emotion/react';
import React from 'react';

import { FieldPageLayout } from 'src/app/graph/insights/fieldPage/FieldPageLayout';
import { InsightsExpandButton } from 'src/app/graph/insights/InsightsExpandButton';
import { useInsightsL3NavState } from 'src/app/graph/operationsPage/insightsL3Nav/useInsightsL3NavState';
import { coordinateNotFoundRouteConfig } from 'src/app/graph/routes';
import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { PageTitle } from 'src/components/common/pageTitle/PageTitle';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { useFieldInsightsFilter } from 'src/hooks/useInsightsFilter';
import { useRouteParams } from 'src/hooks/useRouteParams';
import {
  CoordinateEmptyStateQuery,
  CoordinateEmptyStateQueryVariables,
  CoordinateKind,
} from 'src/lib/graphqlTypes/types';
import { DocsRouter } from 'src/lib/routers';

import { useGraphRef } from '../../hooks/useGraphRef';

import {
  FieldsEmptyStateReason,
  fieldInsightsEmptyStateFragment,
  getFieldsEmptyStateReason,
} from './getFieldsEmptyStateReason';
import { friendlyCoordinateKind } from './shared';

const titleByCoordinateKind = new Map<CoordinateKind, string>([
  [CoordinateKind.ENUM_VALUE, 'No Enum Values Found'],
  [CoordinateKind.INPUT_OBJECT_FIELD, 'No Input Fields Found'],
  [CoordinateKind.OBJECT_FIELD, 'No Fields Found'],
]);

export const CoordinateNotFoundPage = () => {
  const { isOpen, onOpen } = useInsightsL3NavState();
  const routeParams = useRouteParams(coordinateNotFoundRouteConfig);
  const { hasActiveFilters } = useFieldInsightsFilter();
  const coordinateKind =
    routeParams.coordinateKind ?? CoordinateKind.OBJECT_FIELD;
  const graphRef = useGraphRef();

  const { data, loading } = useQuery<
    CoordinateEmptyStateQuery,
    CoordinateEmptyStateQueryVariables
  >(
    gql`
      query CoordinateEmptyStateQuery($serviceId: ID!, $graphVariant: String!) {
        service(id: $serviceId) {
          id
          variant(name: $graphVariant) {
            id
            ...FieldInsightsEmptyStateFragment
          }
        }
      }
      ${fieldInsightsEmptyStateFragment}
    `,
    {
      variables: {
        serviceId: graphRef?.graphId as string,
        graphVariant: graphRef?.graphVariant as string,
      },
      skip: !graphRef,
    },
  );

  if (loading) {
    return <Loading />;
  }

  const emptyStateReason = getFieldsEmptyStateReason({
    routeParams,
    emptyStateVariantFragment: data?.service?.variant,
    hasActiveFilters,
  });

  return (
    <FieldPageLayout>
      <div className="relative flex flex-1 flex-col bg-primary">
        <div className="bg-primary px-8 pb-24">
          <div className="sticky top-0 z-10 bg-primary pt-4">
            <PageTitle
              title=""
              className="border-b-0 px-[0px]"
              leftAdornment={
                !isOpen ? (
                  <Tooltip label="Expand">
                    <InsightsExpandButton onOpen={onOpen} />
                  </Tooltip>
                ) : null
              }
            />
          </div>
        </div>

        <div className="mb-2">
          <NotFound
            image="satellite"
            title={titleByCoordinateKind.get(coordinateKind)}
            subtitle={
              emptyStateReason ===
              FieldsEmptyStateReason.NoCoordinateUsageReported ? (
                <>
                  The router supports usage reporting for{' '}
                  <em>{friendlyCoordinateKind.get(coordinateKind)}s</em> in
                  Router &ge; v1.50.0. To learn more about extended reference
                  reporting,{' '}
                  <Link
                    isExternal
                    to={DocsRouter.path('FieldUsageMetricsAvailability')}
                  >
                    see the docs.
                  </Link>
                </>
              ) : emptyStateReason === FieldsEmptyStateReason.Filters ? (
                <>Try resetting your filters to see more results</>
              ) : (
                <>Try adjusting your time range to see more results</>
              )
            }
          />
        </div>
      </div>
    </FieldPageLayout>
  );
};
